import type { IZynoPermissions } from '@zyno-platform/shared';

import { type Ref, type WritableComputedRef, computed, onUnmounted, ref, watch } from 'vue';

export function usePersistedPreference<T>(
    app: keyof IZynoPermissions,
    key: string,
    storage?: 'local' | 'session',
    json?: boolean,
    defaultValue?: T
): WritableComputedRef<T | undefined> {
    const backend = storage === 'session' ? window.sessionStorage : window.localStorage;
    const storageKey = `zs:${app}:${key}`;

    const trigger = ref(0);

    const handleStorageChange = (event: StorageEvent) => {
        if (event.storageArea === backend && event.key === storageKey) {
            trigger.value++;
        }
    };

    window.addEventListener('storage', handleStorageChange);
    onUnmounted(() => window.removeEventListener('storage', handleStorageChange));

    return computed<T | undefined>({
        get() {
            // oxlint-disable-next-line no-unused-expressions
            trigger.value;
            try {
                const value = backend.getItem(storageKey);
                if (!value) return defaultValue;
                if (json === false) return value as T;
                return JSON.parse(value) as T;
            } catch {
                backend.removeItem(storageKey);
                return defaultValue;
            }
        },

        set(value: T | undefined) {
            if (value === undefined) {
                backend.removeItem(storageKey);
            } else if (json === false) {
                backend.setItem(storageKey, value as string);
            } else {
                backend.setItem(storageKey, JSON.stringify(value));
            }
            trigger.value++;
        }
    });
}

export function usePersistedTenantPreference<T>(
    app: keyof IZynoPermissions,
    key: string,
    storage?: 'local' | 'session',
    json?: boolean,
    defaultValue?: T
): WritableComputedRef<T | undefined> {
    const tid = sessionStorage.getItem('zs:auth:tenantId') ?? localStorage.getItem('zs:auth:lastTenantId') ?? 'unknown';
    return usePersistedPreference<T>(app, `${key}:${tid}`, storage, json, defaultValue);
}

export function usePreferenceSync<T>(pref: WritableComputedRef<T | undefined>, inputRef: Ref<T | undefined>) {
    watch(inputRef, v => {
        pref.value = v;
    });
}
